import { createBrowserRouter, Navigate, Outlet, type RouteObject } from 'react-router';
import { AuthGuard, RoleGuard, SessionGuard } from '@/auth';
import { AppShell } from '@/shell/AppShell';
import { AccessNotProvisioned } from '@/screens/auth/AccessNotProvisioned';
import { AuthError } from '@/screens/auth/AuthError';
import { ForgotPassword } from '@/screens/auth/ForgotPassword';
import { NotPermitted } from '@/screens/auth/NotPermitted';
import { SignIn } from '@/screens/auth/SignIn';
import { SignedOut } from '@/screens/auth/SignedOut';
import { PlaceholderScreen } from '@/screens/placeholders/PlaceholderScreen';
import { UsersScreen } from '@/screens/admin/UsersScreen';
import { InviteUserScreen } from '@/screens/admin/InviteUserScreen';
import { UserDetailScreen } from '@/screens/admin/UserDetailScreen';
import { ShipmentListScreen } from '@/screens/shipments/ShipmentListScreen';
import { ShipmentDetailScreen } from '@/screens/shipments/ShipmentDetailScreen';
import { DriverCollectionsScreen } from '@/screens/driver/DriverCollectionsScreen';
import { LazyBookingReportScreen } from './LazyBookingReportScreen';
import { LazyInvoicingScreen } from './LazyInvoicingScreen';
import {
  LazySupplierInvoiceReviewScreen,
  LazySupplierInvoicesScreen,
} from './LazySupplierInvoicesScreen';
import { RoleLanding } from './RoleLanding';
import {
  FORGOT_PASSWORD_PATH,
  MFA_ENROLL_PATH,
  MFA_REPLACE_AUTHENTICATOR_PATH,
  SECURITY_SETTINGS_PATH,
} from '@/auth';
import { EnrollSecondFactors } from '@/screens/auth/EnrollSecondFactors';
import { ReplaceAuthenticator } from '@/screens/auth/ReplaceAuthenticator';
import { SecuritySettingsScreen } from '@/screens/security/SecuritySettingsScreen';
import { messages } from '@/i18n';

const nav = messages.nav;

/**
 * Public routes — outside every guard.
 *
 * /access-not-provisioned in particular MUST be out here. The guard that
 * redirects to it is SessionGuard, so putting it inside SessionGuard is a
 * redirect loop that appears only in the exact situation the screen exists for.
 */
const publicRoutes: RouteObject[] = [
  { path: '/sign-in', element: <SignIn /> },
  { path: FORGOT_PASSWORD_PATH, element: <ForgotPassword /> },
  { path: '/signed-out', element: <SignedOut /> },
  { path: '/access-not-provisioned', element: <AccessNotProvisioned /> },
  { path: '/auth-error', element: <AuthError /> },
];

/**
 * Guarded routes. Every entry names the roles allowed on it explicitly, and the
 * default is deny — there is no negative test anywhere in this file, and adding
 * one would silently grant a future seventh role everything.
 */
const guardedRoutes: RouteObject[] = [
  {
    element: <RoleGuard allow={['Tollit']} />,
    children: [
      { path: '/control-tower', element: <PlaceholderScreen title={nav.controlTower} /> },
      {
        // docs/shipment-overview-view.md: list view + AWB detail page for the
        // Active shipments tab. Wired to GET /api/v1/shipments(/{awb}) — see
        // docs/shipment-integration-frontend.md.
        path: '/control-tower/shipments',
        element: <Outlet />,
        children: [
          { index: true, element: <ShipmentListScreen /> },
          { path: ':awb', element: <ShipmentDetailScreen /> },
        ],
      },
      {
        // Same shipment list/detail screens as the Active tab above, filtered
        // to status=PalletScanned (variant="delivered") — same filters, sorting
        // and AWB detail route, just a different status.
        path: '/control-tower/delivered',
        element: <Outlet />,
        children: [
          { index: true, element: <ShipmentListScreen variant="delivered" /> },
          { path: ':awb', element: <ShipmentDetailScreen /> },
        ],
      },
      {
        // Same shipment list/detail screens as the Active tab, backed by
        // GET /api/v1/shipments/deviations (variant="deviations") — shipments
        // whose submitted order count deviates from the parcel count. Same
        // filters, sorting and AWB detail route.
        path: '/control-tower/deviations',
        element: <Outlet />,
        children: [
          { index: true, element: <ShipmentListScreen variant="deviations" /> },
          { path: ':awb', element: <ShipmentDetailScreen /> },
        ],
      },
      {
        // docs/booking-report-frontend.md: PreAlert-sourced rebuild of the
        // separate Tollit-dashboard project's "Bookingtall" report. Tollit-only
        // pending the backend's access decision (backend doc, Scope).
        path: '/control-tower/reports/booking',
        element: <LazyBookingReportScreen />,
      },
      {
        // tollit-fulfillment-dotnet-functions:
        // docs/dashboard/2026-10-02-customer-invoice-generation-plan.md §5 —
        // customer invoice basis + summary from our own data, replacing the
        // spreadsheet upload. Tollit-only (Feature.Invoicing). See
        // docs/invoicing-frontend.md.
        path: '/control-tower/finance/invoicing',
        element: <LazyInvoicingScreen />,
      },
      {
        // tollit-fulfillment-dotnet-functions:
        // docs/dashboard/supplier-invoice-validation-frontend-guide.md —
        // upload a ground handler's invoice, review the automatic checks,
        // approve. Tollit-only (Feature.SupplierInvoices). See
        // docs/supplier-invoices-frontend.md.
        path: '/control-tower/finance/supplier-invoices',
        element: <Outlet />,
        children: [
          { index: true, element: <LazySupplierInvoicesScreen /> },
          { path: ':invoiceId', element: <LazySupplierInvoiceReviewScreen /> },
        ],
      },
      // Reuse of the driver's own screen (docs/driver-collections-ui-plan.md) —
      // same API-backed rows, and Tollit staff can confirm/correct collections
      // here too. Split into ready/collected routes the same way the driver's
      // are (docs/driver-collections-split-plan.md §6); the bare path stays as
      // a redirect for old bookmarks and returnTo values.
      {
        path: '/control-tower/collections',
        element: <Navigate to="/control-tower/collections/ready" replace />,
      },
      {
        path: '/control-tower/collections/ready',
        element: <DriverCollectionsScreen variant="ready" overview />,
      },
      {
        path: '/control-tower/collections/collected',
        element: <DriverCollectionsScreen variant="collected" overview />,
      },
      {
        // Conditional on Decision #5. If provisioning stays operator-run SQL,
        // delete this route, src/screens/admin/ and their tests — nothing else
        // depends on them. Built ahead of that decision, on mock data, so there
        // is a concrete screen to evaluate it against; see UsersScreen's own
        // notice to users that it isn't wired to a backend yet.
        path: '/control-tower/access',
        element: <Outlet />,
        children: [
          { index: true, element: <UsersScreen /> },
          { path: 'invite', element: <InviteUserScreen /> },
          { path: ':userId', element: <UserDetailScreen /> },
        ],
      },
    ],
  },
  {
    element: <RoleGuard allow={['Driver']} />,
    children: [
      {
        // docs/driver-collections-ui-plan.md: every driver sees every ready
        // shipment (no assignment), with confirm/correct dialogs backed by
        // src/api/endpoints/collections.ts. Whether confirmations queue
        // offline remains a product decision that constrains this layer: a
        // queued action still needs an unexpired token when it eventually
        // submits.
        path: '/driver/collections',
        element: <Navigate to="/driver/collections/ready" replace />,
      },
      {
        path: '/driver/collections/ready',
        element: <DriverCollectionsScreen variant="ready" />,
      },
      {
        path: '/driver/collections/collected',
        element: <DriverCollectionsScreen variant="collected" />,
      },
    ],
  },
  {
    element: <RoleGuard allow={['GroundHandler']} />,
    children: [
      {
        // docs/ground-handler-shipments-frontend.md: reuse of ShipmentListScreen
        // and ShipmentDetailScreen behind scope="handler" — wired to
        // GET /api/v1/ground-handler/shipments(/{awb}).
        path: '/handler/shipments',
        element: <Outlet />,
        children: [
          { index: true, element: <ShipmentListScreen scope="handler" /> },
          { path: ':awb', element: <ShipmentDetailScreen scope="handler" /> },
        ],
      },
    ],
  },
  {
    element: <RoleGuard allow={['ReceivingTerminal']} />,
    children: [
      {
        // Same full-visibility shipment list/detail as the Tollit control tower
        // (scope="tollit", the default) — Receiving terminal sees every
        // customer's shipments, not just its own AWB prefix.
        path: '/terminal/shipments',
        element: <Outlet />,
        children: [
          { index: true, element: <ShipmentListScreen /> },
          { path: ':awb', element: <ShipmentDetailScreen /> },
        ],
      },
    ],
  },
  {
    element: <RoleGuard allow={['LastMileCustomerService']} />,
    children: [
      {
        // Same full-visibility shipment list/detail as the Tollit control
        // tower (scope="tollit", the default) — Last-mile customer service
        // sees every customer's shipments, not just its own.
        path: '/last-mile/shipments',
        element: <Outlet />,
        children: [
          { index: true, element: <ShipmentListScreen /> },
          { path: ':awb', element: <ShipmentDetailScreen /> },
        ],
      },
    ],
  },
  {
    element: <RoleGuard allow={['SenderCustomer']} />,
    children: [
      {
        // docs/customer-shipments-frontend.md: reuse of ShipmentListScreen and
        // ShipmentDetailScreen behind scope="customer" — wired to
        // GET /api/v1/customer/shipments(/{awb}).
        path: '/shipments',
        element: <Outlet />,
        children: [
          { index: true, element: <ShipmentListScreen scope="customer" /> },
          { path: ':awb', element: <ShipmentDetailScreen scope="customer" /> },
        ],
      },
    ],
  },
];

export const routes: RouteObject[] = [
  ...publicRoutes,
  {
    element: <AuthGuard />,
    children: [
      // The MFA enrollment routes need a signed-in user (AuthGuard) but MUST
      // sit outside SessionGuard: /me refuses the user until they have
      // enrolled, and SessionGuard is what redirects here — nesting them
      // inside it is the same loop as /access-not-provisioned above.
      { path: MFA_ENROLL_PATH, element: <EnrollSecondFactors /> },
      { path: MFA_REPLACE_AUTHENTICATOR_PATH, element: <ReplaceAuthenticator /> },
      {
        element: <SessionGuard />,
        children: [
          // /not-permitted lives inside SessionGuard on purpose: it needs the
          // session to offer a way back to the role's own landing route.
          { path: '/not-permitted', element: <NotPermitted /> },
          {
            element: <AppShell />,
            children: [
              { index: true, element: <RoleLanding /> },
              // Every role manages its own second factors, so no RoleGuard.
              { path: SECURITY_SETTINGS_PATH, element: <SecuritySettingsScreen /> },
              ...guardedRoutes,
            ],
          },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
];

export const router = createBrowserRouter(routes);
